import axios from "axios";

/**
 * Outcome of a slug → company lookup, distinguishing the two access-gate
 * failures the `GET /c/{slug}` resolver returns (dale `get_company_with_access`):
 * - `not-found`: no company owns this slug (404).
 * - `forbidden`: the slug exists but the caller can't reach that company (403).
 * - `error`: any other failure (network / 5xx).
 * - `null`: the query hasn't errored.
 *
 * Kept in its own axios-only module (no store/React imports) so the 404/403
 * mapping is directly unit-testable.
 */
type CompanyBySlugErrorKind = "not-found" | "forbidden" | "error";

/**
 * Classifies a `useGetCompanyBySlug` failure into a discriminated kind. Pure so the
 * 404/403 mapping is unit-testable without React Query.
 */
export const classifyCompanyBySlugError = (
  cause: unknown,
): CompanyBySlugErrorKind | null => {
  if (!cause) return null;
  if (axios.isAxiosError(cause)) {
    const status = cause.response?.status;
    if (status === 404) return "not-found";
    if (status === 403) return "forbidden";
  }
  return "error";
};

/** How many times a transient (network / 5xx) slug lookup retries before giving up. */
export const COMPANY_BY_SLUG_MAX_RETRIES = 2;

/**
 * React Query `retry` predicate for the slug resolver. The access-gate verdicts
 * (404/403) — and any other 4xx client error — are stable for a given slug +
 * caller, so they surface immediately without retrying. Transient failures
 * (network errors, 5xx) retry up to `COMPANY_BY_SLUG_MAX_RETRIES` so a blip on
 * this cold-load path recovers instead of hard-failing.
 */
export const shouldRetryCompanyBySlug = (
  failureCount: number,
  cause: unknown,
): boolean => {
  if (axios.isAxiosError(cause)) {
    const status = cause.response?.status;
    if (status !== undefined && status >= 400 && status < 500) {
      return false;
    }
  }
  return failureCount < COMPANY_BY_SLUG_MAX_RETRIES;
};
